import { Route, Routes } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ConsoleShell } from '@/components/layout/ConsoleShell';
import { RedirectHome, RedirectIfSignedIn, RequireAuth, RequireRole } from './guards';
import { LoginPage } from '@/features/auth/LoginPage';
import { SignUpPage } from '@/features/auth/SignUpPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { SetupPage } from '@/features/setup/SetupPage';
import { OverviewPage } from '@/features/overview/OverviewPage';
import { VehiclesPage } from '@/features/vehicles/VehiclesPage';
import { DeviceDetailPage } from '@/features/devices/DeviceDetailPage';
import { CrashListPage } from '@/features/crashes/CrashListPage';
import { CrashDetailPage } from '@/features/crashes/CrashDetailPage';
import { AlertsPage } from '@/features/alerts/AlertsPage';
import { FleetPage } from '@/features/fleet/FleetPage';
import { RespondPage } from '@/features/respond/RespondPage';
import { OperationsPage } from '@/features/admin/OperationsPage';
import { CustomersPage } from '@/features/admin/CustomersPage';
import { CustomerDetailPage } from '@/features/admin/CustomerDetailPage';
import { AdminDevicesPage } from '@/features/admin/DevicesPage';
import { ProvisioningPage } from '@/features/admin/ProvisioningPage';
import { RolesPage } from '@/features/admin/RolesPage';
import { MaintenancePage } from '@/features/admin/MaintenancePage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { NotFoundPage } from '@/features/misc/NotFoundPage';
import { CancelCrashPage } from '@/features/misc/CancelCrashPage';
import { PrivacyNoticePage } from '@/features/misc/PrivacyNoticePage';

const OWNER_SIDE = ['vehicle_owner', 'fleet_manager', 'administrator'] as const;

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RedirectIfSignedIn>
            <LoginPage />
          </RedirectIfSignedIn>
        }
      />
      <Route
        path="/signup"
        element={
          <RedirectIfSignedIn>
            <SignUpPage />
          </RedirectIfSignedIn>
        }
      />
      {/* Reachable while signed out by definition — someone who could sign in would
          not be here. Not wrapped in RedirectIfSignedIn: a reset link arriving in a
          browser with a live session should still work. */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* The internal operations console. Its own shell, and one role gate for the
          whole branch — an administrator runs the product rather than owning a
          vehicle in it, so this is their app, not a section of the customer one. */}
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <RequireRole roles={['administrator']}>
              <ConsoleShell />
            </RequireRole>
          </RequireAuth>
        }
      >
        <Route index element={<OperationsPage />} />
        <Route path="devices" element={<AdminDevicesPage />} />
        <Route path="provisioning" element={<ProvisioningPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:customerId" element={<CustomerDetailPage />} />
        <Route path="roles" element={<RolesPage />} />
        <Route path="maintenance" element={<MaintenancePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* One-time cancel link from the crash-alert SMS. No auth — the token is it. */}
      <Route path="/c/:token" element={<CancelCrashPage />} />

      {/* The notice must be readable before an account exists. */}
      <Route path="/privacy" element={<PrivacyNoticePage />} />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<RedirectHome />} />
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/alerts" element={<AlertsPage />} />

        <Route
          path="/overview"
          element={
            <RequireRole roles={OWNER_SIDE}>
              <OverviewPage />
            </RequireRole>
          }
        />
        <Route
          path="/vehicles"
          element={
            <RequireRole roles={OWNER_SIDE}>
              <VehiclesPage />
            </RequireRole>
          }
        />
        <Route
          path="/devices/:deviceId"
          element={
            <RequireRole roles={OWNER_SIDE}>
              <DeviceDetailPage />
            </RequireRole>
          }
        />
        <Route
          path="/crashes"
          element={
            <RequireRole roles={OWNER_SIDE}>
              <CrashListPage />
            </RequireRole>
          }
        />
        <Route
          path="/crashes/:crashId"
          element={
            <RequireRole roles={['vehicle_owner', 'fleet_manager', 'administrator', 'emergency_responder']}>
              <CrashDetailPage />
            </RequireRole>
          }
        />

        <Route
          path="/fleet"
          element={
            <RequireRole roles={['fleet_manager', 'administrator']}>
              <FleetPage />
            </RequireRole>
          }
        />
        <Route
          path="/respond"
          element={
            <RequireRole roles={['emergency_responder', 'administrator']}>
              <RespondPage />
            </RequireRole>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
